// East-side continuation toward Okubo. Existing footprints and map topology are authoritative.
import type {HdBuilding} from '../../common/src/data';
import type {LightFn} from './hd-registry';
import {type G,K,HT,px,frame,bands,disc,text} from './hd-kit';
import {panel,label,fan,pipe,window} from './hd-hachi-study';
export const NORTHEAST_STUDY_BUILDINGS=['ke-bar','ke-hotel','ke-snack','ke-park','ke-flats','ke-laundry'] as const;
function slats(g:G,x:number,y:number,w:number,h:number){panel(g,x,y,w,h,K.metalLo);for(let j=4;j<h-3;j+=5)px(g,x+3,y+j,w-6,1,K.metalHi);}
function balcony(g:G,x:number,y:number,w:number){px(g,x,y,w,2,K.metalHi);for(let j=0;j<w;j+=8)px(g,x+j,y,2,12,K.metal);px(g,x,y+12,w,3,K.metalLo);}
export function paintKabukiNortheast(g:G,b:HdBuilding,x:number,y:number,L:LightFn){
 if(!(NORTHEAST_STUDY_BUILDINGS as readonly string[]).includes(b.id))return;
 const w=b.w*HT,h=b.h*HT,rh=(b.h-b.face)*HT,fy=y+rh;
 g.save();g.beginPath();g.rect(x,y,w,h);g.clip();
 if(b.id!=='ke-park')pipe(g,x+w-12,y+12,rh-22);
 if(b.id==='ke-bar'){
  // Keep the original roof board, adding a compact extraction unit behind it.
  fan(g,x+112,y+24);px(g,x+143,y+32,23,7,K.metalLo);
  bands(g,x+2,fy+2,w-4,h-rh-4,K.woodHi,K.woodLo,6);
  for(let j=8;j<w-6;j+=12)px(g,x+j,fy+4,2,h-rh-8,K.woodLo);
  label(g,'バー',x+65,fy+6,62,K.pink,2);
  // This face abuts the hotel: bottle windows, not a newly advertised street entrance.
  for(const xx of [x+12,x+111]){window(g,xx,fy+35,68,24,K.amber);for(let j=8;j<60;j+=12){px(g,xx+j,fy+44,5,10,K.woodLo);px(g,xx+j+1,fy+41,2,4,K.metalHi);}px(g,xx+3,fy+53,62,3,K.woodHi);}
  L(x+70,fy+11,52,15,K.pink,.2,false);
 }else if(b.id==='ke-hotel'){
  panel(g,x+22,y+30,82,49,K.concreteLo);fan(g,x+29,y+38);slats(g,x+65,y+38,31,31);
  window(g,x+122,y+33,42,43,K.glassLo);px(g,x+125,y+52,36,2,K.metalHi);
  bands(g,x+2,fy+2,w-4,h-rh-4,K.wall,K.wallLo,8);
  label(g,'ホテル',x+42,fy+7,108,K.pink,2);
  for(let row=0;row<2;row++)for(let j=0;j<4;j++){const xx=x+14+j*44,yy=fy+38+row*27;window(g,xx,yy,31,21,(row+j)%3?K.amber:K.glassLo);px(g,xx+3,yy+2,5,17,K.woodLo);px(g,xx+24,yy+2,4,17,K.woodLo);}
  L(x+49,fy+13,94,17,K.pink,.2,false);
 }else if(b.id==='ke-snack'){
  for(let j=0;j<3;j++){fan(g,x+20+j*53,y+33);px(g,x+29+j*53,y+60,7,20,K.metalLo);}
  bands(g,x+2,fy+2,w-4,h-rh-4,K.woodHi,K.woodLo,8);
  const names=['バー','ヤキトリ','ゲーム'];
  for(let j=0;j<3;j++){const xx=x+j*64,c=j===1?K.amber:K.pink;
   label(g,names[j],xx+5,fy+8,54,c,1);px(g,xx+3,fy+33,58,5,K.ink);px(g,xx+4,fy+33,56,1,c);
   window(g,xx+9,fy+43,25,48,c);px(g,xx+27,fy+67,2,3,K.amber);
   window(g,xx+39,fy+45,18,28,K.amber);px(g,xx+39,fy+72,18,3,K.woodHi);
   disc(g,xx+51,fy+84,5,7,K.amber);L(xx+8,fy+13,48,10,c,.18,true);
  }
 }else if(b.id==='ke-park'){
  // Existing rooftop cars and bays stay; reinforce the deck edges and service stair.
  for(const xx of [x+6,x+w-9]){px(g,xx,y+9,3,rh-18,K.concreteHi);for(let j=15;j<rh-12;j+=15)px(g,xx-2,y+j,7,3,K.metalLo);}
  bands(g,x+2,fy+2,w-4,h-rh-4,K.concrete,K.concreteLo,8);
  label(g,'P 24H',x+49,fy+6,94,K.amber,2);
  // Ventilated parking side faces the flats: closed deck, no fake drive-in ramp.
  for(let row=0;row<2;row++){const yy=fy+37+row*27;panel(g,x+11,yy,w-22,21,K.ink);px(g,x+15,yy+3,w-30,2,K.warmDim);for(let j=0;j<4;j++){const xx=x+18+j*42;px(g,xx,yy+12,29,7,K.metalLo);px(g,xx+4,yy+10,21,3,K.glassLo);px(g,xx+34,yy,4,21,K.concrete);}}
 }else if(b.id==='ke-flats'){
  panel(g,x+25,y+55,w-51,53,K.woodLo);for(let j=5;j<48;j+=9)px(g,x+29,y+55+j,w-59,1,K.woodHi);
  px(g,x+31,y+51,w-63,1,K.metalHi);for(let j=0;j<4;j++)px(g,x+36+j*29,y+54,16,22,j%2?K.concreteHi:K.woodLo);
  balcony(g,x+27,y+98,w-55);
  bands(g,x+2,fy+2,w-4,h-rh-4,K.concrete,K.wallLo,6);
  for(let j=0;j<3;j++){const xx=x+14+j*58;window(g,xx,fy+7,43,28,j===1?K.glassLo:K.amber);px(g,xx+4,fy+9,6,23,K.woodLo);balcony(g,xx-2,fy+30,47);}
  for(let j=0;j<3;j++)slats(g,x+21+j*58,fy+47,28,12);
 }else{
  // Laundry extraction and a small rooflight; no cyan decorative glass.
  panel(g,x+22,y+31,80,43,K.metalLo);fan(g,x+28,y+38);fan(g,x+63,y+38);
  window(g,x+122,y+27,41,47,K.glassLo);px(g,x+125,y+48,35,2,K.metalHi);
  bands(g,x+2,fy+2,w-4,h-rh-4,K.concrete,K.wallLo,7);
  label(g,'ランドリー',x+18,fy+6,w-36,K.white,2);
  panel(g,x+12,fy+36,w-24,54,K.glassLo);
  for(let j=0;j<4;j++){const xx=x+19+j*40;panel(g,xx,fy+43,33,40,K.concreteHi);disc(g,xx+16,fy+65,11,11,K.ink);disc(g,xx+16,fy+65,8,8,K.glass);disc(g,xx+13,fy+63,3,4,K.metalHi);px(g,xx+5,fy+48,13,2,K.metalLo);px(g,xx+24,fy+48,3,2,K.amber);}
  px(g,x+15,fy+36,w-30,2,K.warmHi);L(x+19,fy+38,w-38,6,K.amber,.15,true);
 }
 frame(g,x+2,fy+2,w-4,h-rh-4,K.ink);px(g,x+2,y+h-4,w-4,3,K.ink);g.restore();
}
